<template>
  <div class="pay_box">
    <div class="pay_select_list">
      <div
        v-for="(paySelect, index) in paySelectList" :key="index"
        :class="['pay_select', paySelect.name, { 'selected': isPaySelected(paySelect.type) }]"
        @click="clickPaySelect(paySelect.type)">
        <i class="logo" />
        <span>{{ paySelect.text }}</span>
        <i class="corner" />
        <i class="hook" />
      </div>
      <span class="agreement">购买即同意<span class="link" @click="clickMemberProtocol">《可牛会员协议》</span></span>
    </div>
    <div class="qr_container">
      <qrcode-vue
        v-show="qr" class="qr" :value="qr" level="H"
        :size="102"
      />
    </div>
    <div class="prices_container">
      <div class="prices">
        <div class="price">实付：￥ <span class="font_big">{{ price }}</span></div>
        <div class="price_minus"><span>已优惠：￥ {{ priceMinus }}</span></div>
      </div>
      <div class="icon_scan_container">
        <i class="icon_scan" />
        <div>
          <span>打开</span>
          <span class="text_scan_app" :style="{ color: colorTextScanApp }">{{ textScan }}</span>
          <br>
          扫一扫进行付款
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue'
import { CacheSetUserInfo, CacheSessionGetSource } from '@/common/cache'
import api from '@/api'
import { report } from '@/report'
import { ClientSetUserInfo, ClientOpenBrowser, ClientSendMsgToTable } from '@/api/clientApi.js'
import { urlMemberProtocol } from '@/common/resources.js'
import { reportServerId } from '@/common/business.js'

const payapp = 'keniuoffice'
const payStatus = {
  success: 'paid',
  wait: 'paying',
  refunding: 'refunding',
  refund: 'refund',
  fail: 'fail'
}

const payTypeMap = {
  wxPay: 1,
  aliPay: 2
}

export default {
  name: 'PayBox',

  components: { QrcodeVue },

  props: {
    price: {
      type: [String, Number],
      default: 152
    },
    priceMinus: {
      type: [String, Number],
      default: 299
    },
    packageId: {
      type: Number,
      default: 0
    },
    canGetCoupon: {
      type: Boolean,
      default: false
    },
    pageid: {
      type: [String, Number],
      default: ''
    },
    otherReportParams: {
      type: Object,
      default: () => ({})
    },
  },

  data () {
    return {
      intervalQueryPay: null,
      timeDiff: 0,
      payTypeMap,
      payTypeSelected: payTypeMap.wxPay,
      qr: '',
      wxQr: '',
      aliQr: ''
    }
  },

  computed: {
    userInfo () {
      return this.$store.state.userInfo
    },
    pricesCoupon () {
      return [5.5, 3.3]
    },
    paySelectList () {
      return [
        {
          type: payTypeMap.wxPay,
          name: 'wx_pay',
          text: '微信支付',
        },
        {
          type: payTypeMap.aliPay,
          name: 'ali_pay',
          text: '支付宝支付',
        }
      ]
    },
    textScan () {
      return this.payTypeSelected === payTypeMap.wxPay ? '微信APP' : '支付宝APP'
    },
    colorTextScanApp () {
      return this.payTypeSelected === payTypeMap.wxPay ? '#0DCD0B' : '#3E8AF4'
    },
    isSelectForever () {
      return this.$store.getters['buyDialog/isSelectForever']
    },
    matOrigin () {
      return Number(this.$route.query.origin)
    }
  },

  watch: {
    packageId (newValue) {
      if (newValue) {
        this.setWxQr()
      }
    }
  },

  created () {
    this.setWxQr()
  },

  beforeDestroy () {
    this.clearTimer()
  },

  methods: {
    /** 点击-选择支付类型 **/
    clickPaySelect (payType) {
      this.payTypeSelected = payType
      if (this.payTypeSelected === payTypeMap.wxPay) {
        /** 上报-点击微信支付 **/
        report('pay', { actiontype: 'payclick', actionid: 3, pageid: this.pageid, ext: '', ...this.otherReportParams })
        this.setWxQr()
      } else {
        /** 上报-点击支付宝支付 **/
        report('pay', { actiontype: 'payclick', actionid: 4, pageid: this.pageid, ext: '', ...this.otherReportParams })
        this.setAliQr()
      }
    },

    clickMemberProtocol () {
      ClientOpenBrowser(urlMemberProtocol)
    },

    async clickCouponReceive () {
      // actiontype-1: 点击，clickid-1: 点击礼包
      const paySource = this.$route.query.paysource
      report('pay', { actiontype: 2, clickid: 1, paysource: paySource, ext: '', ...this.otherReportParams })
      await this.setNewCoupon()
      this.$store.dispatch('updateCouponState')
      this.$emit('reload')
    },

    isPaySelected (payType) {
      return this.payTypeSelected === payType
    },

    getServiceTime () {
      return api.common.getServiceTime().then(res => res?.data?.timestamp * 1000)
    },

    async setWxQr () {
      this.qr = await this.getWxQrcodeByPackageId(this.packageId)
    },

    async setAliQr () {
      this.qr = await this.getAliPayQrcodeByPackageId(this.packageId)
    },

    async getWxQrcodeByPackageId (packageId) {
      const productIdWxPay = await this.getProductId(packageId, payTypeMap.wxPay)
      return await this.getWxQrcode(productIdWxPay)
    },

    async getAliPayQrcodeByPackageId (packageId) {
      const productIdAliPay = await this.getProductId(packageId, payTypeMap.aliPay)
      return await this.getAliQrcode(productIdAliPay)
    },

    // 需要根据套餐id和支付类型获取产品id
    getProductId (packageId, payType) {
      const source = CacheSessionGetSource()
      const req = { data: { biztype: 1, paytype: payType, id: packageId, source, source_provider: this.matOrigin } }
      return api.member.prepare(req).then(res => res?.data?.product_id)
    },

    getWxQrcode (productId) {
      return api.member.wxPay({ data: { payapp, product_id: productId } }).then(res => {
        const data = res.data
        if (!data || !data.order_id || !data.wx_resp || !data.wx_resp.code_url) {
          this.$toast({ desc: '获取支付二维码失败，请刷新重试或联系客服' })
        }
        // 执行结果轮询
        this.getPayResult(data.order_id, payTypeMap.wxPay)
        return data.wx_resp.code_url
      }).catch(() => {
        this.$toast({ desc: '获取微信二维码失败，请刷新重试或联系客服' })
      })
    },

    getAliQrcode (productId) {
      return api.member.aliPay({ data: { payapp, product_id: productId } }).then(res => {
        const data = res.data
        // 执行结果轮询
        this.getPayResult(data.order_id, payTypeMap.aliPay)
        return data.ali_resp.code_url
      }).catch(() => {
        this.$toast({ desc: '获取支付宝二维码失败，请刷新重试或联系客服' })
      })
    },

    payFail () {
      this.$toast({ desc: '支付失败，请刷新重试或联系客服' })
      this.clearTimer()
    },

    getPayResult (id, type) {
      this.clearTimer()
      this.intervalQueryPay = setInterval(() => {
        api.member.payQuery({ data: { payapp, order_id: id } }).then(res => {
          const data = res.data

          if (!data || !data.order || !data.order.status) {
            this.payFail()
            this.clearTimer()
            return
          }

          if (data.order.status === payStatus.fail) {
            this.payFail()
            this.clearTimer()
            return
          }

          // 其他状态暂不处理
          if (data.order.status === payStatus.success) {
            const order = data.order
            const paySource = this.$route.query.paysource
            report('payresult', { order_id: id, order_time: order.pay_time, order_num: order.fee, order_paytype: type === 1 ? 'weixin' : 'alipay', pageid: this.pageid, paysource: paySource, ext: '', ...this.otherReportParams })
            this.clearTimer()
            reportServerId()
            this.resetUserInfo()
            if (this.isSelectForever) {
              this.updateLeftCountByUsePackage()
            }
          }
        })
      }, 3000)
    },

    resetUserInfo () {
      api.user.privliege().then(res => {
        const data = res.data
        if (!data.name || !data.end_date) {
          this.$toast({ desc: '支付已经成功，请刷新页面' })
        }
        const lastUserInfo = { ...this.userInfo, isVip: true, privliegeName: data.name, privliegeEndTime: data.end_date }

        this.$store.commit('setUserInfo', lastUserInfo)
        CacheSetUserInfo(lastUserInfo)
        ClientSetUserInfo(lastUserInfo)
          .then(() => {
            ClientSendMsgToTable('matDetail', 'logined')
          })
        this.$store.dispatch('initBuyBtn') // 支付后终身会员隐藏成为会员按钮
        this.clearTimer()
        this.$emit('close')
      }).catch(() => {
        this.$toast({ desc: '支付已经成功，请刷新页面或者退出重新登录' })
      }).finally(() => {
        this.clearTimer()
      })
    },

    updateLeftCountByUsePackage () {
      this.$store.dispatch('buyDialog/updateLeftCountByUsePackage')
    },

    clearTimer () {
      clearInterval(this.intervalQueryPay)
    }
  },
}
</script>

<style lang="scss" scoped>
.pay_box { display: flex; align-items: center; justify-content: center; width: 536px; height: 130px; background: #FFFFFF; border-radius: 8px;
  .pay_select_list { .pay_select { position: relative; box-sizing: border-box; display: flex; align-items: center; width: 125px; height: 36px; font-size: 14px; font-weight: 400; color: #666; cursor: pointer; background: #fff; border: 1px solid rgba($color:#000, $alpha:0.1); border-radius: 4px;
      .logo { width: 18px; margin: 0 4px 0 18px; }
      .corner { position: absolute; right: 0; bottom: 0; width: 0; height: 0; border: 10px solid transparent; }
      .hook { position: absolute; right: 1px; bottom: 2px; display: none; width: 9.1px; height: 6.9px; background-image: url("~@/assets/images/icon_hook.png"); background-size: cover; }
      &.selected { .hook { display: block; }
      }
      &.wx_pay { &.selected { background: #EBFFEB; border-color: #09BB07;
          .corner { border-color: transparent #09BB07 #09BB07 transparent; }
        }
        .logo { height: 15px; background: url(~@/assets/images/icon_activity_wechat.png) no-repeat; }
      }
      &.ali_pay { margin-top: 12px;
        &.selected { background: #EEF5FF; border-color: #3E8AF4;
          .corner { border-color: transparent #3E8AF4 #3E8AF4 transparent; }
        }
        .logo { height: 18px; background: url(~@/assets/images/icon_activity_alipay.png) no-repeat; }
      }
    }
    .agreement { margin-left: -10px;
      font-size: 12px;
      .link { color: #4a96e8; cursor: pointer; }
     }
  }
  .qr_container { width: 112px; height: 112px; margin-left: 39px;
    .qr { box-sizing: border-box; width: 112px; height: 112px; padding: 4px; background-repeat: no-repeat; background-position: center center; background-size: 88px 88px; }
  }
  .prices_container { display: flex; flex-direction: column; margin-left: 13px; font-size: 12px; font-weight: 400;
    .prices { position: relative;
      .price { font-weight: bold; color: #FF003F;
        .font_big { font-size: 23px; }
      }
      .price_minus { opacity: 0.7; }
    }
    .icon_scan_container { display: flex; margin-top: 15px;
      .icon_scan { width: 32px; height: 30px; margin-right: 12px; background: url(~@/assets/images/icon_scan_code.png); }
      .text_scan_app { display: inline-block; width: 72px; margin-left: 5px; }
    }
  }
}

</style>
